import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check, Minus } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Reveal, LineReveal, FadeIn } from "../components/Reveal";
import { Overline, PrimaryButton, GhostLink, CtaBand } from "../components/Primitives";
import { Marquee } from "../components/Marquee";
import { IMG, SERVICE_IMAGES } from "../lib/assets";
import { Seo } from "../components/Seo";

const easeLux = [0.22, 1, 0.36, 1];

const Hero = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const lines = t("home.heroLines");

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[640px] overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={IMG.hero} alt="Kenosis sanctuary" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-kenosis-black/85 via-kenosis-black/65 to-kenosis-black" />
        <div className="absolute inset-0 bg-kenosis-black/40" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-center px-6 md:px-10">
        <h1 className="mt-8 font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-light leading-[1.0] tracking-tight text-kenosis-ivory text-outline-strong">
          <LineReveal lines={lines} delay={0.5} lineClassName="" />
        </h1>

        <FadeIn delay={1.4}>
          <p className="mt-10 max-w-xl font-sans text-base md:text-lg font-light leading-relaxed text-kenosis-ivory/85 text-outline">
            {t("home.heroSub")}
          </p>
        </FadeIn>

        <FadeIn delay={1.7}>
          <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <PrimaryButton testid="hero-cta-primary" onClick={() => navigate("/apply")}>{t("common.cta")}</PrimaryButton>
            <GhostLink testid="hero-cta-secondary" onClick={() => navigate("/method")}>{t("common.ctaSecondary")}</GhostLink>
          </div>
        </FadeIn>
      </motion.div>
    </section>
  );
};

const BrandStatement = () => {
  const { t } = useLang();
  return (
    <section className="relative py-28 md:py-40">
      <div className="mx-auto max-w-[1100px] px-6 md:px-10 text-center">
        <Reveal><Overline className="justify-center">{t("home.brandOverline")}</Overline></Reveal>
        <Reveal delay={0.1}>
          <p className="mx-auto mt-10 max-w-4xl font-serif text-3xl sm:text-4xl md:text-5xl font-light leading-[1.25] tracking-tight text-kenosis-ivory">
            {t("home.brandStatement")}
          </p>
        </Reveal>
      </div>
    </section>
  );
};

const Dimensions = () => {
  const { t } = useLang();
  const dims = t("home.dimensions");
  const imgs = [IMG.structural, IMG.inner, IMG.metabolic];
  return (
    <section data-testid="dimensions-section" className="relative border-t border-white/10 py-24 md:py-36">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="max-w-3xl">
          <Reveal><Overline>{t("home.dimensionsOverline")}</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-8 font-serif text-4xl md:text-5xl font-light leading-tight tracking-tight text-kenosis-ivory">
              {t("home.dimensionsTitle")}
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-xl font-sans text-base font-light text-kenosis-muted">{t("home.dimensionsIntro")}</p>
          </Reveal>
        </div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {dims.map((d, i) => (
            <Reveal key={i} delay={i * 0.1} className="group bg-kenosis-black p-8 md:p-10 transition-colors duration-500 hover:bg-kenosis-surface">
              <div className="relative mb-8 aspect-[4/3] overflow-hidden">
                <img src={imgs[i]} alt={d.title} className="h-full w-full object-cover opacity-70 transition-all duration-[1.2s] ease-out group-hover:scale-105 group-hover:opacity-90" />
                <span className="absolute left-4 top-4 font-serif text-2xl text-kenosis-gold">{d.num}</span>
              </div>
              <h3 className="font-serif text-2xl md:text-3xl font-normal text-kenosis-ivory">{d.title}</h3>
              <p className="mt-4 font-sans text-sm font-light leading-relaxed text-kenosis-muted">{d.purpose}</p>
              <ul className="mt-6 space-y-2 border-t border-white/10 pt-6">
                {d.tools.map((tool, k) => (
                  <li key={k} className="flex items-center gap-3 font-sans text-xs text-kenosis-ivory/60">
                    <span className="h-1 w-1 rounded-full bg-kenosis-gold/70" />
                    {tool}
                  </li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Qualification = () => {
  const { t } = useLang();
  const forYou = t("home.forYou");
  const notForYou = t("home.notForYou");
  return (
    <section data-testid="qualification-section" className="relative border-t border-white/10 py-24 md:py-36">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <Reveal><Overline>{t("home.qualOverline")}</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-8 max-w-2xl font-serif text-4xl md:text-5xl font-light tracking-tight text-kenosis-ivory">{t("home.qualTitle")}</h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          <div className="bg-kenosis-black p-8 md:p-12">
            <h3 className="font-sans text-xs uppercase tracking-[0.2em] text-kenosis-gold">{t("home.forYouTitle")}</h3>
            <ul className="mt-8 space-y-5">
              {forYou.map((item, i) => (
                <Reveal as="li" key={i} delay={i * 0.05} className="flex items-start gap-4">
                  <Check size={16} className="mt-1 shrink-0 text-kenosis-gold" />
                  <span className="font-sans text-base font-light text-kenosis-ivory/85">{item}</span>
                </Reveal>
              ))}
            </ul>
          </div>
          <div className="bg-kenosis-surface p-8 md:p-12">
            <h3 className="font-sans text-xs uppercase tracking-[0.2em] text-kenosis-muted">{t("home.notForYouTitle")}</h3>
            <ul className="mt-8 space-y-5">
              {notForYou.map((item, i) => (
                <Reveal as="li" key={i} delay={i * 0.05} className="flex items-start gap-4">
                  <Minus size={16} className="mt-1 shrink-0 text-kenosis-muted/60" />
                  <span className="font-sans text-base font-light text-kenosis-muted">{item}</span>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

const ServicesOverview = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const services = t("home.services");
  return (
    <section className="relative border-t border-white/10 py-24 md:py-36">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div>
            <Reveal><Overline>{t("home.servicesOverline")}</Overline></Reveal>
            <Reveal delay={0.1}><h2 className="mt-8 font-serif text-4xl md:text-5xl font-light tracking-tight text-kenosis-ivory">{t("home.servicesTitle")}</h2></Reveal>
          </div>
          <Reveal delay={0.2}><GhostLink testid="services-overview-link" onClick={() => navigate("/services")}>{t("nav.services")}</GhostLink></Reveal>
        </div>

        <div className="mt-16 border-t border-white/10">
          {services.map((s, i) => (
            <Reveal key={i} delay={i * 0.05} className="group grid grid-cols-1 md:grid-cols-12 items-center gap-6 md:gap-4 border-b border-white/10 py-8 transition-colors hover:bg-kenosis-surface/40 px-2 -mx-2 cursor-default">
              <div className="md:col-span-2">
                <div className="relative aspect-[4/3] overflow-hidden border border-white/10 bg-kenosis-black">
                  <img
                    src={SERVICE_IMAGES[i]}
                    alt={s.name}
                    loading="lazy"
                    className="h-full w-full object-cover opacity-80 transition-all duration-[1.2s] ease-out group-hover:scale-105 group-hover:opacity-100"
                  />
                </div>
              </div>
              <span className="md:col-span-1 font-serif text-xl text-kenosis-gold/70">0{i + 1}</span>
              <h3 className="md:col-span-3 font-serif text-2xl md:text-3xl font-normal text-kenosis-ivory transition-colors group-hover:text-kenosis-gold">{s.name}</h3>
              <p className="md:col-span-2 font-sans text-xs uppercase tracking-[0.15em] text-kenosis-muted">{s.meta}</p>
              <p className="md:col-span-4 font-sans text-sm font-light text-kenosis-ivory/70">{s.desc}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Manifesto = () => {
  const { t } = useLang();
  const items = t("home.manifesto");
  return (
    <section data-testid="manifesto-section" className="relative border-t border-white/10 py-24 md:py-36">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <Reveal><Overline>{t("home.manifestoOverline")}</Overline></Reveal>
        <Reveal delay={0.1}><h2 className="mt-8 font-serif text-4xl md:text-5xl font-light tracking-tight text-kenosis-ivory">{t("home.manifestoTitle")}</h2></Reveal>
        <div className="mt-16">
          {items.map((m, i) => (
            <Reveal key={i} delay={i * 0.05} className="group grid grid-cols-1 md:grid-cols-12 gap-6 border-t border-white/10 py-10">
              <span className="md:col-span-2 font-serif text-6xl md:text-7xl font-light leading-none text-kenosis-gold/25 transition-colors duration-500 group-hover:text-kenosis-gold/60">{m.num}</span>
              <h3 className="md:col-span-4 font-serif text-2xl md:text-3xl font-light text-kenosis-ivory">{m.title}</h3>
              <p className="md:col-span-6 self-center font-sans text-base font-light leading-relaxed text-kenosis-muted">{m.text}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const FounderIntro = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const creds = t("home.founderCreds");
  return (
    <section className="relative border-t border-white/10 py-24 md:py-36">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
        <Reveal className="md:col-span-5">
          <div className="relative aspect-[3/4] overflow-hidden">
            <img src={IMG.founder} alt="F. G. Aaron Giuga" className="h-full w-full object-cover object-[center_25%]" onError={(e) => { e.currentTarget.src = IMG.inner; }} />
            <div className="absolute inset-0 bg-gradient-to-t from-kenosis-black/60 to-transparent" />
          </div>
        </Reveal>
        <div className="md:col-span-7">
          <Reveal><Overline>{t("home.founderOverline")}</Overline></Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 font-serif text-3xl md:text-4xl font-light italic leading-snug text-kenosis-ivory">{t("home.founderQuote")}</p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-xl font-sans text-base font-light leading-relaxed text-kenosis-muted">{t("home.founderBio")}</p>
          </Reveal>
          <Reveal delay={0.3}>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {creds.map((c, i) => (
                <li key={i} className="flex items-start gap-3 font-sans text-sm text-kenosis-ivory/70">
                  <span className="mt-1.5 h-1 w-1 rounded-full bg-kenosis-gold" />{c}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.4}>
            <div className="mt-10"><GhostLink testid="founder-intro-link" onClick={() => navigate("/founder")}>{t("home.founderCta")}</GhostLink></div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const SelectedWords = () => {
  const { t } = useLang();
  const words = t("home.words");
  return (
    <section className="relative border-t border-white/10 py-24 md:py-36">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <Reveal><Overline>{t("home.wordsOverline")}</Overline></Reveal>
        <Reveal delay={0.1}><h2 className="mt-8 font-serif text-4xl md:text-5xl font-light tracking-tight text-kenosis-ivory">{t("home.wordsTitle")}</h2></Reveal>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
          {words.map((w, i) => {
            const Tag = w.href ? "a" : "div";
            const linkProps = w.href ? { href: w.href, target: "_blank", rel: "noopener noreferrer" } : {};
            return (
              <Reveal key={i} delay={i * 0.1}>
                <Tag
                  {...linkProps}
                  data-testid={w.href ? `words-link-${i}` : undefined}
                  className={`block border border-white/10 bg-kenosis-surface p-8 md:p-10 transition-colors duration-500 hover:border-kenosis-gold/30 ${w.href ? "group cursor-pointer" : ""}`}
                >
                  <span className="font-serif text-5xl leading-none text-kenosis-gold/40">&ldquo;</span>
                  <p className="mt-4 font-serif text-xl font-light italic leading-snug text-kenosis-ivory/90">{w.quote}</p>
                  <div className="mt-8 flex items-center justify-between gap-4">
                    <p className={`font-sans text-xs uppercase tracking-[0.2em] ${w.href ? "text-kenosis-gold" : "text-kenosis-muted"}`}>{w.name}</p>
                    {w.href && (
                      <span className="inline-flex shrink-0 items-center gap-1 font-sans text-[0.65rem] uppercase tracking-[0.2em] text-kenosis-gold transition-transform duration-300 group-hover:translate-x-1">
                        Read
                        <span aria-hidden>&rarr;</span>
                      </span>
                    )}
                  </div>
                </Tag>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo pageKey="home" path="/" jsonLd />
      <Hero />
      <BrandStatement />
      <Marquee />
      <Dimensions />
      <Qualification />
      <ServicesOverview />
      <Manifesto />
      <FounderIntro />
      <SelectedWords />
      <CtaBand />
    </div>
  );
}
